import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import { LoadingChart } from '../exploration/loading-chart.js';

/**
 * Preparation progress for the 3D world, without changing the server's clock.
 * `show` blocks the world behind a full overlay; `note`/`endNote` report background work in a corner chip that leaves
 * the menus usable, and never downgrade or hide an overlay that something else is waiting behind.
 * Each takes an optional fraction (0–1) for the bar; within one showing the bar only moves forward.
 * The full overlay is opaque: shader warm-up draws the whole scene behind it. Given a map (`chart`), it shows the destination on
 * parchment under the map's name, with the progress inked as the party's route in, instead of the bar.
 */
export class WorldLoading {
  private overlay = document.createElement('div');
  private message = document.createElement('p');
  private bar = document.createElement('div');
  private fill = document.createElement('span');
  private title = document.createElement('h2');
  private subtitle = document.createElement('p');
  private chartView: LoadingChart | null = null;
  private charted: { map: MapDefinition; destination: Point } | null = null;
  private blocking = false;
  private fraction = 0;
  private shown = 0;
  private listener: ((message: string) => void) | null = null;
  /** `host` holds the overlay; `stage` is made inert while it blocks. */
  constructor(host = document.getElementById('app')!, private stageElement?: HTMLElement) {
    this.overlay.className = 'world-loading'; this.overlay.hidden = true;
    this.bar.className = 'world-loading-bar'; this.bar.setAttribute('role', 'progressbar'); this.bar.setAttribute('aria-valuemin', '0'); this.bar.setAttribute('aria-valuemax', '100');
    this.title.className = 'world-loading-title'; this.subtitle.className = 'world-loading-subtitle';
    this.bar.append(this.fill); this.overlay.append(this.title, this.subtitle, this.message, this.bar); host.append(this.overlay);
    this.progress(0);
  }
  show(message: string, fraction?: number) {
    if (!this.blocking) this.fraction = 0;
    this.blocking = true; this.message.textContent = message; this.progress(fraction);
    this.overlay.classList.remove('passive'); this.overlay.classList.toggle('charted', !!this.charted); this.overlay.hidden = false; this.setInert(true);
  }
  /** The map the next full overlays show, and where on it the party will stand; `null` shows only the message and bar. */
  chart(map: MapDefinition | null, destination: Point = { x: 0, z: 0 }) {
    this.charted = map?.scenery ? { map, destination } : null;
    if (this.charted && !this.chartView) { this.chartView = new LoadingChart(); this.subtitle.after(this.chartView.canvas); }
    this.title.textContent = this.charted?.map.name ?? ''; this.subtitle.textContent = this.charted?.map.subtitle ?? '';
    if (this.blocking) { this.overlay.classList.toggle('charted', !!this.charted); this.drawChart(); }
  }
  hide() {
    this.blocking = false; this.overlay.hidden = true; this.overlay.classList.remove('passive', 'retry'); this.setInert(false);
  }
  /** Background progress: the text always updates, but only an idle overlay turns into the corner chip. */
  note(message: string, fraction?: number) {
    if (this.overlay.hidden) this.fraction = 0;
    this.message.textContent = message; this.listener?.(message);
    if (!this.blocking) { this.progress(fraction); this.overlay.classList.remove('charted'); this.overlay.classList.add('passive'); this.overlay.hidden = false; }
  }
  /** Also hands every background note to `listener` (the door's caption, while it is shut). */
  mirror(listener: (message: string) => void) { this.listener = listener; }
  /** Background work finished: clears the chip, leaving a blocking overlay for whoever raised it. */
  endNote() { if (!this.blocking) this.hide(); }
  retry(message: string): Promise<void> {
    this.show(message); this.overlay.classList.add('retry');
    const button = document.createElement('button'); button.className = 'primary'; button.textContent = 'Retry loading';
    this.overlay.append(button);
    return new Promise(resolve => button.addEventListener('click', () => { button.remove(); this.overlay.classList.remove('retry'); this.fraction = 0; this.progress(); resolve(); }, { once: true }));
  }
  private setInert(inert: boolean) { const stage = this.stageElement ?? document.getElementById('stage'); if (stage) stage.inert = inert; }
  private progress(fraction?: number) {
    if (fraction !== undefined && Number.isFinite(fraction)) this.fraction = Math.max(this.fraction, Math.min(1, Math.max(0, fraction)));
    // A fresh showing starts empty at once rather than sliding back from the last one.
    const restart = this.fraction < this.shown;
    if (restart) this.fill.style.transition = 'none';
    this.fill.style.transform = `scaleX(${this.fraction})`; this.shown = this.fraction;
    if (restart) { void this.fill.offsetWidth; this.fill.style.transition = ''; }
    this.bar.setAttribute('aria-valuenow', String(Math.round(this.fraction * 100)));
    this.drawChart();
  }
  private drawChart() { if (this.blocking && this.charted) this.chartView?.draw(this.charted.map, this.charted.destination, this.fraction); }
}
